import currency from "currency.js";
import { logger } from "../../utils/logger";
import { ethers } from "ethers";
import { COLORS } from "../../constants/design/colors";

import { CollateralAssetResponse } from "../../codegen-api";
import { DepositWithdrawCollaterals, IsUSDC } from "../../constants/addresses";
import { ChainIdEnum, SOCKET_DEPOSIT_CHAINS } from "../../enums/chain";
import { IDepositERC20MultichainResponse } from "../../hooks/contracts/useDeposit";
import { ID_TO_CHAINS } from "../../hooks/wallet/useWallet";
import { getAssetShortName } from "../../utils/asset/assets";
import { toTokenUnits } from "../../utils/decimalString";
import { CHAIN_EXPLORER_URLS, getTxExplorerUrl } from "../../utils/chain";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import { scheduleAccountRefresh } from "../../utils/refreshAccount";
import { supportedSigningChainIds } from "../../utils/wallet/connectors";
import { insufficientLimitImpl } from "./helpers";
import { RunDepositOrApproveParams } from "./types";

// Extracted verbatim from the deposit branch of `handleDepositOrApprove`
// (everything after the approval short-circuit). No control flow / behaviour
// changes: it runs inside the same try/catch/finally in runDepositOrApprove.
export const executeDeposit = async (
  data: { amount: string },
  {
    setDepositError,
    useUnifiedVaultFlow,
    isNativeDepositAsset,
    decimals,
    chainId,
    t,
    assetToDeposit,
    depositToUnifiedVault,
    depositETHOptimismOrArbitrum,
    depositETH,
    l1Contract,
    l2Address,
    getAssetPermitSignature,
    depositERC20OptimismOrArbitrum,
    yieldVaultToggle,
    approvedAmountAndSignature,
    depositERC20WithPermit,
    depositERC20,
    setPendingDepositTxURL,
    mutate,
    onHideClicked,
    addToast,
    playSound,
  }: RunDepositOrApproveParams
) => {
  const insufficientLimit = (
    response: IDepositERC20MultichainResponse
  ) =>
    insufficientLimitImpl(response, {
      chainId,
      t,
      decimals,
      assetToDeposit,
      setDepositError,
    });

  // DEPOSIT
  let depositTx: ethers.ContractTransaction | undefined;

  // Floored to the token's decimals; `parseUnits` on the typed text threw a raw
  // ethers error for exponent text or one decimal too many.
  const depositAmtBN = toTokenUnits(data.amount, decimals);
  if (!depositAmtBN || depositAmtBN.isZero()) {
    setDepositError(t("invalid_amount", { defaultValue: "Enter a valid amount" }));
    return;
  }

  if (useUnifiedVaultFlow) {
    logger.debug("[DEPOSIT DEBUG] Using UnifiedVault deposit flow", {
      amount: depositAmtBN.toString(),
      amountFormatted: data.amount,
      isNativeDepositAsset,
    });
    try {
      depositTx = await depositToUnifiedVault(depositAmtBN.toString());
      logger.debug(
        "[DEPOSIT DEBUG] UnifiedVault deposit tx created:",
        depositTx?.hash
      );
    } catch (unifiedVaultError: any) {
      console.error("[DEPOSIT ERROR] UnifiedVault deposit failed:", {
        message: unifiedVaultError?.message,
        code: unifiedVaultError?.code,
        data: unifiedVaultError?.data,
        reason: unifiedVaultError?.reason,
      });
      throw unifiedVaultError;
    }
  } else if (assetToDeposit === CollateralAssetResponse.Weth) {
    // DEPOSIT ETH
    const amtETH = toTokenUnits(data.amount, 18) ?? depositAmtBN;
    if (SOCKET_DEPOSIT_CHAINS.includes(chainId as ChainIdEnum)) {
      const response = await depositETHOptimismOrArbitrum(amtETH);
      if (response?.tx) {
        depositTx = response.tx;
      } else if (response?.insufficientLimit) {
        insufficientLimit(response);
      }
    } else {
      depositTx = await depositETH(amtETH);
    }
  } else {
    // DEPOSIT ERC20
    logger.debug("[DEPOSIT DEBUG] ERC20 Deposit details:", {
      assetToDeposit,
      depositAmtBN: depositAmtBN.toString(),
      l1ContractAddress: l1Contract?.address,
      l2Address,
      hasPermitSignature: getAssetPermitSignature !== undefined,
      approvedDeadline: approvedAmountAndSignature.deadline,
      approvedSignature: approvedAmountAndSignature.signature,
      chainId,
      isSocketDepositChain: SOCKET_DEPOSIT_CHAINS.includes(
        chainId as ChainIdEnum
      ),
      useUnifiedVaultFlow,
    });

    // Decide to deposit permit or depositerc20
    if (
      SOCKET_DEPOSIT_CHAINS.includes(chainId as ChainIdEnum) &&
      l1Contract?.address
    ) {
      // Cross chain deposit
      const response = await depositERC20OptimismOrArbitrum(
        depositAmtBN,
        l1Contract.address,
        assetToDeposit,
        assetToDeposit === DepositWithdrawCollaterals.Usdc ||
          assetToDeposit === DepositWithdrawCollaterals.NativeUsdc
          ? yieldVaultToggle
          : false
      );
      if (response?.tx) {
        depositTx = response.tx;
      } else if (response?.insufficientLimit) {
        const chainEnum = chainId as ChainIdEnum;
        const otherNetworks = supportedSigningChainIds
          .filter((c) => c !== chainEnum)
          .map((c) => ID_TO_CHAINS[c]);
        const otherNetworksText =
          otherNetworks.length === 2
            ? otherNetworks.join(` ${t("or")} `)
            : otherNetworks.join(", ");
        const currentNetworkName = ID_TO_CHAINS[chainEnum];

        // No limit
        if (response.insufficientLimit.isZero()) {
          setDepositError(
            t("no_bridge_capacity", {
              currentChain: currentNetworkName,
              otherChains: otherNetworksText,
            })
          );
        } else {
          const limitStr = ethers.utils.formatUnits(
            response.insufficientLimit,
            decimals
          );
          let text = t("insufficient_bridge_capacity", {
            chain: ID_TO_CHAINS[chainEnum],
          });
          text += t("reduce_deposit_size", {
            size: `${currency(limitStr).format({
              symbol: "",
            })} ${assetToDeposit}`,
            otherChains: otherNetworksText,
          });
          setDepositError(text);
        }
      }
    } else if (l1Contract?.address && l2Address) {
      if (
        getAssetPermitSignature !== undefined &&
        approvedAmountAndSignature.deadline &&
        approvedAmountAndSignature.signature
      ) {
        // deposit with permit
        depositTx = await depositERC20WithPermit(
          l1Contract.address,
          l2Address,
          depositAmtBN,
          approvedAmountAndSignature.deadline,
          approvedAmountAndSignature.signature,
          IsUSDC(assetToDeposit) ? yieldVaultToggle : false
        );
      } else {
        // deposit erc20
        depositTx = await depositERC20(
          l1Contract.address,
          l2Address,
          depositAmtBN,
          IsUSDC(assetToDeposit) ? yieldVaultToggle : false
        );
      }
    }
  }

  logger.debug("[DEPOSIT DEBUG] Deposit transaction created:", {
    depositTx: depositTx ? depositTx.hash : "undefined",
    depositTxExists: !!depositTx,
  });

  // Only hides if a deposit tx was triggered
  if (depositTx) {
    if (yieldVaultToggle) {
      playSound("yv_deposit", true);
    }
    const explorerUrl = CHAIN_EXPLORER_URLS[chainId as ChainIdEnum];
    setPendingDepositTxURL(`${explorerUrl}/tx/${depositTx.hash}`);
    await depositTx.wait(1);

    // Backend indexes the deposit a few seconds after on-chain
    // confirmation — schedule retries so the trading-page balance
    // refreshes without waiting for the next 30s polling tick.
    scheduleAccountRefresh(mutate);

    // Dismiss modal and show success
    setPendingDepositTxURL(undefined);
    onHideClicked?.();
    addToast(
      {
        type: ToastEnum.SIMPLE,
        header: t("deposit_initiated"),
        subheader: (
          <span>
            {`${currency(data.amount).format({ symbol: "" })} ${getAssetShortName(assetToDeposit)} is credited. `}
            <a href={getTxExplorerUrl(depositTx.hash, chainId as ChainIdEnum)} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.link.one, textDecoration: "underline" }}>
              View transaction
            </a>
          </span>
        ),
        status: ToastStatusEnum.SUCCESS,
      },
      10000
    );
  }
};
